import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Loader2, Camera, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ProofOfDeliveryDialog — buyer uploads a photo (and optional note) confirming
 * they received the item. Server-side this auto-stamps stage=delivered.
 */
const ProofOfDeliveryDialog = ({ open, onClose, txId, onSubmitted }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [note, setNote] = useState("");

  const reset = () => {
    setPhotoUrl(null); setPhotoPreview(null); setNote(""); setUploading(false); setSubmitting(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handlePickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setPhotoPreview(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setPhotoUrl(fullUrl);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed — try a smaller image");
      setPhotoPreview(null);
    } finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!photoUrl) return;
    setSubmitting(true);
    try {
      await api.post(`/transactions/${txId}/proof_of_delivery`, { photo_url: photoUrl, note: note.trim() || undefined });
      toast.success("Delivery confirmed — thank you!");
      onSubmitted?.();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save proof");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent data-testid="proof-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#2A3D36]" /> Confirm delivery
          </DialogTitle>
          <DialogDescription>
            Upload a photo of the item you received so the seller and our team have a record. Optional — but it helps everyone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {photoPreview ? (
            <div className="relative">
              <img src={photoPreview} alt="Delivery proof" className="w-full max-h-72 object-contain rounded-md bg-[#F3EFE9]" data-testid="proof-preview" />
              {uploading && (
                <div className="absolute inset-0 bg-black/40 grid place-items-center rounded-md text-white text-sm gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" /> Uploading…
                </div>
              )}
              <button
                type="button"
                onClick={() => { setPhotoPreview(null); setPhotoUrl(null); }}
                className="absolute top-2 right-2 text-xs bg-white/95 rounded px-2 py-1 hover:bg-white"
              >
                Change photo
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="proof-pick-btn"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-black/15 rounded-md p-8 grid place-items-center text-center hover:border-[#C05A3C] hover:bg-[#C05A3C]/5 transition-colors"
            >
              <Camera className="w-7 h-7 text-muted-foreground mb-2" />
              <div className="font-semibold text-sm">Choose a photo</div>
              <div className="text-xs text-muted-foreground mt-1">JPG, PNG or WebP up to 8MB</div>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={handlePickFile} data-testid="proof-file-input" />
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note (optional)</label>
            <textarea
              data-testid="proof-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Everything arrived in good condition…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-black/15 bg-white text-sm focus:border-[#C05A3C] focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={submitting || uploading}>Cancel</Button>
          <Button
            data-testid="proof-submit-btn"
            onClick={handleSubmit}
            disabled={!photoUrl || uploading || submitting}
            className="bg-[#2A3D36] hover:bg-[#1F2D27] text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirm delivery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProofOfDeliveryDialog;
